import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';

const ManageDataPage = () => {
  const { user, isAuthenticated } = useApp();
  const [email, setEmail] = useState('');
  const [selectedData, setSelectedData] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const handleExport = async () => {
    if (!user?.id) return;
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}/export-data`);
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minted-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Data export error:', err);
      alert('Could not export your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const dataTypes = [
    { id: 'messages', label: 'Message History', description: 'All conversations with buyers and sellers' },
    { id: 'listings', label: 'Product Listings', description: 'All your current and past listings' },
    { id: 'purchase_history', label: 'Purchase History', description: 'Records of items you have bought' },
    { id: 'search_history', label: 'Search History', description: 'Your in-app search history' },
    { id: 'notifications', label: 'Notifications', description: 'All notification history' },
    { id: 'saved_items', label: 'Saved Items', description: 'Items you have saved or favorited' },
  ];

  const toggleDataType = (id) => {
    setSelectedData(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedData.length === 0) return;
    
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/account/data-deletion-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: isAuthenticated ? user?.email : email,
          user_id: user?.id || null,
          data_types: selectedData
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting data deletion request:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout title="Request Submitted" showBack>
        <div className="bg-white px-4 py-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Received</h2>
            <p className="text-gray-600 mb-6">
              We have received your data deletion request. Our team will process your request within 30 days and send you a confirmation email once completed.
            </p>
            <p className="text-sm text-gray-500">
              Your account will remain active. Only the selected data will be deleted.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Manage My Data" showBack>
      <div className="bg-white px-4 py-6">
        <div className="max-w-md mx-auto">
          {isAuthenticated && (
            <div className="mb-8 border border-emerald-100 bg-emerald-50/50 rounded-xl p-4" data-testid="data-export-section">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Download My Data</h2>
              <p className="text-gray-600 text-sm mb-3">
                Export a copy of all your personal data (profile, listings, orders, messages, wallet history) as a JSON file. This is your right under the Australian Privacy Act and GDPR.
              </p>
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="bg-emerald-800 hover:bg-emerald-700"
                data-testid="download-my-data-btn"
              >
                {exporting ? 'Preparing export...' : 'Download My Data'}
              </Button>
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Specific Data</h2>
            <p className="text-gray-600 text-sm">
              Select the types of data you want to delete. Your account will remain active.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isAuthenticated && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your account email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-minted-500"
                  required
                />
              </div>
            )}

            {isAuthenticated && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">
                  Logged in as: <span className="font-medium">{user?.email}</span>
                </p>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Select data to delete:</p>
              {dataTypes.map((dataType) => (
                <label
                  key={dataType.id}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedData.includes(dataType.id)
                      ? 'border-minted-500 bg-minted-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedData.includes(dataType.id)}
                    onChange={() => toggleDataType(dataType.id)}
                    className="mt-1 h-4 w-4 text-minted-600 rounded border-gray-300"
                  />
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{dataType.label}</p>
                    <p className="text-sm text-gray-500">{dataType.description}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Your account and profile information will not be affected. Only the selected data types will be deleted.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || selectedData.length === 0 || (!isAuthenticated && !email)}
              className="w-full bg-minted-600 hover:bg-minted-700 text-white mt-4"
            >
              {loading ? 'Submitting...' : `Request Deletion of ${selectedData.length} Data Type${selectedData.length !== 1 ? 's' : ''}`}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-500 text-center">
              Want to delete your entire account?{' '}
              <a href="/delete-account" className="text-minted-600 hover:underline">
                Request account deletion
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManageDataPage;
